/**
 * The arrangements every textbook draws, as data.
 *
 * These are to this bench what guided experiments are to the chemistry bench:
 * adding one should be a row here, not code. Each carries the claim the book
 * makes about it — "MA = 2" — so the bench can be CHECKED against the book
 * rather than believed, which is the only reason a student should trust either.
 *
 * The x positions are not chosen by eye. A rope leaves a sheave at its RIM, so
 * a run between two stops is only vertical when their CONTACT POINTS share an
 * x — which is (r₁ + r₂) between two sheaves and r between a sheave and
 * anything without one. `thread()` is that one line of arithmetic applied
 * along the threading order, which is why these draw straight ropes instead of
 * a fan.
 */
import { constraintsOf } from './constraints';
import { remeasure } from './join';
import { solve } from './solve';
import { bodiesOf, isFree, partById as byId } from './world';
import { SHEAVE_R, SHEAVE_R_ON_FACE, WEIGHT_ON_FACE_HALF } from './kinds';
import { apexHeight, SurfaceData } from './kinds/surface';
import { defOf } from './registry';
import { Link, Part, PortRef, Rope, World } from './types';
import { effectivePort, partById, portAt } from './world';

export interface Preset {
    key: string;
    label: string;
    /** What the textbook says about it, checked by the bench's own readouts. */
    claim: string;
    build: () => World;
    /**
     * Its runs are NOT all vertical or horizontal, on purpose.
     *
     * Every other preset here is axis-aligned, which is what makes its
     * timeline exact and is worth a test of its own. A bench whose whole point
     * is the angle a slung wheel hangs at cannot be, and saying so here is the
     * difference between an exception and a broken promise.
     */
    slanted?: true;
}

const G = 9.8;
/** One sheave, at the size the bench is scaled by. */
const R = SHEAVE_R;
const ROOM = { ceiling: 4, floor: 0, width: 6 };

const P = (partId: string, portId: string): PortRef => ({ partId, portId });

const anchorAt = (id: string, x: number, label?: string): Part =>
    ({ id, kind: 'anchor', at: { x, y: ROOM.ceiling }, data: {}, ...(label ? { label } : {}) });
const sheaveAt = (id: string, x: number, y: number): Part =>
    ({ id, kind: 'sheave', at: { x, y }, data: { radius: R, mass: 0 } });
const weightAt = (id: string, x: number, y: number, mass: number, label?: string): Part =>
    ({ id, kind: 'weight', at: { x, y }, data: { mass }, ...(label ? { label } : {}) });
const endAt = (id: string, x: number, y: number, label?: string): Part =>
    ({ id, kind: 'ropeEnd', at: { x, y }, data: {}, ...(label ? { label } : {}) });
const balanceAt = (id: string, x: number, y: number, label?: string): Part =>
    ({ id, kind: 'springBalance', at: { x, y }, data: {}, ...(label ? { label } : {}) });
/** `at` is the middle of the BASE, which stands on the floor — see
 *  `kinds/surface.ts`. */
const surfaceAt = (id: string, kind: string, x: number, base: number, left: number, right: number, height: number, mu: number): Part<SurfaceData> =>
    ({ id, kind, at: { x, y: ROOM.floor }, data: { base, left, right, height, muTop: mu, muLeft: mu, muRight: mu } });

/**
 * A block standing on a face, `s` metres along it from the middle.
 *
 * The seat is arithmetic the surface owns — where a block sits is where its
 * own body clears the face — so it is asked for rather than guessed at, and a
 * ramp at any angle needs no second version of this line.
 */
function standing(id: string, on: Part<SurfaceData>, face: string, s: number, mass: number, label?: string): Part {
    const seat = defOf(on).supports?.(on as never, shell([on]), face) as { seat(s: number, c: number): { x: number; y: number } };
    return {
        id,
        kind: 'weight',
        at: seat.seat(s, WEIGHT_ON_FACE_HALF),
        data: { mass },
        support: on.id,
        face,
        ...(label ? { label } : {}),
    };
}
const bond = (id: string, hangs: PortRef, holds: PortRef): Link => ({ id, type: 'bond', hangs, holds });

/**
 * How far the rope touches from the part's centre, across the bench.
 *
 * The PORT, which for a wheel is a rim — and for a VERTICAL run that is
 * exactly where the tangent lands, because the radius to a tangent point is
 * perpendicular to the run. Every run `thread` makes is vertical, so this is
 * exact here and nowhere near a general answer: `segmentsOf` solves the
 * tangent for runs at any angle, and this is the one case where the answer is
 * a property of the part alone.
 */
const offset = (w: World, ref: PortRef): number => {
    const part = partById(w, ref.partId);
    const port = portAt(w, ref);
    return part && port ? port.at.x - part.at.x : 0;
};

/**
 * Thread a rope and slide the parts under it so every run is vertical.
 *
 * A stop in the middle of a path is used TWICE — the rope arrives on one rim
 * and leaves from the other — so the outgoing offset is the mirror of the
 * incoming one. That is the same rule `segmentsOf` uses to measure the rope,
 * which is why these two never disagree about where the rope is.
 */
function thread(w: World, id: string, path: PortRef[], startX: number, label?: string): World {
    const x = new Map<string, number>();
    let cur = startX;
    path.forEach((ref, i) => {
        x.set(ref.partId, cur);
        const nextRef = path[i + 1];
        if (!nextRef) return;
        const out = offset(w, effectivePort(w, ref, i > 0));
        cur = cur + out - offset(w, nextRef);
    });
    const moved: World = {
        ...w,
        parts: w.parts.map(p => (x.has(p.id) ? { ...p, at: { ...p.at, x: x.get(p.id) as number } } : p)),
    };
    const rope: Rope = { id, type: 'rope', path, length: 0, ...(label ? { label } : {}) };
    return remeasure({ ...moved, links: [...moved.links, rope] });
}

/** Put a part directly under (or over) another — a hook above its sheave. */
const over = (w: World, id: string, aboveId: string): World => ({
    ...w,
    parts: w.parts.map(p => (p.id === id ? { ...p, at: { ...p.at, x: partById(w, aboveId)?.at.x ?? p.at.x } } : p)),
});

const shell = (parts: Part[], links: Link[] = []): World => ({ parts, links, gravity: G, room: ROOM });

export function fixedPulley(): World {
    let w = shell(
        [anchorAt('roof', 3), sheaveAt('p1', 3, 3.25), weightAt('load', 2.7, 1.5, 5), endAt('hand', 3.3, 1.5)],
        [bond('b1', P('p1', 'axle'), P('roof', 'eye'))],
    );
    w = thread(w, 'r1', [P('load', 'hook'), P('p1', 'rimL'), P('hand', 'end')], 2.7, 'the rope');
    return remeasure(over(w, 'roof', 'p1'));
}

export function movablePulley(): World {
    let w = shell(
        [anchorAt('roof', 2.7), sheaveAt('p1', 3, 2.3), weightAt('load', 3, 1.3, 5), endAt('hand', 3.3, 3.5)],
        [bond('b1', P('load', 'hook'), P('p1', 'axle'))],
    );
    w = thread(w, 'r1', [P('roof', 'eye'), P('p1', 'rimL'), P('hand', 'end')], 2.7, 'the rope');
    return remeasure(over(w, 'load', 'p1'));
}

export function atwood(mass1 = 4, mass2 = 5): World {
    let w = shell(
        [anchorAt('roof', 3), sheaveAt('p1', 3, 3.25), weightAt('m1', 2.7, 1.4, mass1, 'm₁'), weightAt('m2', 3.3, 2.4, mass2, 'm₂')],
        [bond('b1', P('p1', 'axle'), P('roof', 'eye'))],
    );
    w = thread(w, 'r1', [P('m1', 'hook'), P('p1', 'rimL'), P('m2', 'hook')], 2.7, 'the rope');
    return remeasure(over(w, 'roof', 'p1'));
}

export function movableTied(): World {
    let w = shell(
        [
            anchorAt('roofA', 2.4), sheaveAt('pMov', 2.7, 2.1), sheaveAt('pFix', 3.3, 3.25),
            anchorAt('roofB', 3.3), endAt('hand', 3.6, 1.3), weightAt('load', 2.7, 1.1, 4),
        ],
        [bond('b1', P('pFix', 'axle'), P('roofB', 'eye')), bond('b2', P('load', 'hook'), P('pMov', 'axle'))],
    );
    w = thread(w, 'r1', [P('roofA', 'eye'), P('pMov', 'rimL'), P('pFix', 'rimL'), P('hand', 'end')], 2.4, 'the fall');
    return remeasure(over(over(w, 'roofB', 'pFix'), 'load', 'pMov'));
}

/**
 * A block and tackle with `n` rope segments holding the lower block.
 *
 * The threading is the arithmetic a student does when they count segments, so
 * it is written out rather than tabulated:
 *
 *   even n — tie to the ceiling, then down under d0, up over u0, down under
 *            d1 … and out to the hand. Each lower sheave gives two segments.
 *   odd  n — tie to the LOWER BLOCK itself, then up over u0, down under d0 …
 *            The tie-off is the odd segment, which is exactly why an odd MA
 *            needs the rope to start at the load rather than at the ceiling.
 */
export function tackle(n: number): World {
    const topY = 3.25, botY = 1.8;
    const tieToRoof = n % 2 === 0;
    const lowers = Math.floor(n / 2);
    const uppers = tieToRoof ? lowers : lowers + 1;
    const upperIds = Array.from({ length: uppers }, (_, i) => `u${i}`);
    const lowerIds = Array.from({ length: lowers }, (_, i) => `d${i}`);

    const parts: Part[] = [
        // Two hooks, when there ARE two: one the fall is tied to, one the fixed
        // sheaves hang from. They are separate parts because they are at
        // different places on the beam, and pretending otherwise would slant
        // the first run.
        //
        // With an ODD number of segments the fall is tied to the lower block
        // instead, and the roof hook has nothing to tie. It was built anyway —
        // so the bench carried a hook named "the tie-off" that was joined to
        // nothing, carried 0.0 N, and sat next to the tie-off it was not.
        ...(tieToRoof ? [anchorAt('beam', 3, 'the tie-off')] : []),
        anchorAt('crown', 3, 'the top block'),
    ];
    upperIds.forEach(id => parts.push(sheaveAt(id, 3, topY)));
    /* The lower block carries the dead end when there is an odd number of
     * segments, and it carries it on a BECKET — an eye on the shell, one bay
     * out from the wheel, so that run rises vertically like the rest of the
     * fall. See `SheaveData.becket`. One bay is the rim plus the gap to the
     * next run: R to the rim, 2R to the line beyond it. */
    lowerIds.forEach((id, i) => parts.push(
        !tieToRoof && i === 0 ? { ...sheaveAt(id, 3, botY), data: { radius: R, mass: 0, becket: -3 * R } } : sheaveAt(id, 3, botY),
    ));
    parts.push(weightAt('load', 3, botY - 0.8, 12));
    parts.push(endAt('hand', 3, 1.05));

    const links: Link[] = upperIds.map(id => bond(`b-${id}`, P(id, 'axle'), P('crown', 'eye')));
    lowerIds.slice(1).forEach(id => links.push(bond(`b-${id}`, P(id, 'axle'), P('d0', 'axle'))));
    links.push(bond('b-load', P('load', 'hook'), P('d0', 'axle')));

    // Down a lower sheave's LEFT rim, up an upper's right, and so on, so the
    // runs stay side by side instead of crossing.
    /* The dead end goes on the lower block's BECKET, never on its axle and
     * never on the load's own hook.
     *
     * The axle is the block's centre line — the mean of the runs wrapping it —
     * so a fall tied there leaves at a slant and stops carrying its whole
     * tension upward: T went 39.2 N to 52.6 N on this preset when it did. The
     * load's hook keeps the run vertical but puts the LOAD on that line, a bay
     * to the side of the block it hangs from, which is not what a tackle looks
     * like. The becket is where a real one is spliced, and it is both. */
    const path: PortRef[] = [tieToRoof ? P('beam', 'eye') : P('d0', 'becket')];
    for (let k = 0; k < n; k++) {
        const lower = tieToRoof ? k % 2 === 0 : k % 2 === 1;
        const i = tieToRoof ? Math.floor(k / 2) : Math.floor((k + (lower ? -1 : 0)) / 2);
        path.push(lower ? P(`d${i}`, 'rimL') : P(`u${i}`, 'rimL'));
    }
    path.push(P('hand', 'end'));

    let w = shell(parts, links);
    /* Centre the whole run before laying it out. `thread` puts the FIRST STOP
     * on the line it is given, and a becket is not at its part's centre — so
     * the offset comes back out of the start, or the whole tackle slides a bay
     * left of the middle of the bench. */
    const span = 2 * R * (path.length - 2);
    w = thread(w, 'r1', path, 3 - span / 2 - offset(w, path[0]), 'the fall');
    const meanX = (ids: string[]): number => ids.reduce((s, i) => s + (partById(w, i)?.at.x ?? 3), 0) / Math.max(1, ids.length);
    return remeasure({
        ...w,
        parts: w.parts.map(p => {
            if (p.id === 'crown') return { ...p, at: { ...p.at, x: meanX(upperIds) } };
            // For odd n the tie-off is ON the load block, so the chain has
            // already placed it and re-centring would slant the run it starts.
            if (p.id === 'load' && tieToRoof) return { ...p, at: { ...p.at, x: meanX(lowerIds) } };
            return p;
        }),
    });
}

/**
 * A chain of `n` movable pulleys, each on its own string — MA 2ⁿ.
 *
 * The load hangs from the lowest wheel. Each string is tied to the ceiling,
 * runs down and under its wheel, and up to the AXLE of the wheel above; the
 * top wheel's string goes up to the hand. Every wheel halves what the string
 * above it carries, which is the whole of the claim.
 *
 * The x arithmetic is `thread`'s: a string meets its wheel a radius to the left
 * of the centre and leaves a radius to the right, so each wheel stands one
 * radius to the right of the one below, and the next string's hook one radius
 * to the left of its wheel. Every run is then vertical.
 */
export function movableSystem(n: number): World {
    const X0 = 2.6;
    const top = 3.1, gap = n > 1 ? Math.min(0.8, 2.1 / (n - 1)) : 0;
    const wheelY = (k: number): number => top - (n - 1 - k) * gap;
    const wheels = Array.from({ length: n }, (_, k) => sheaveAt(`m${k}`, X0 + k * R, wheelY(k)));
    const hooks = Array.from({ length: n }, (_, k) => anchorAt(`roof${k}`, X0 + (k - 1) * R));
    const load = weightAt('load', X0, wheelY(0) - 0.8, 10);
    const hand = endAt('hand', X0 + n * R, Math.min(ROOM.ceiling - 0.3, top + 0.6));
    let w = shell([...hooks, ...wheels, load, hand], [bond('b-load', P('load', 'hook'), P('m0', 'axle'))]);
    for (let k = 0; k < n; k++) {
        const upTo = k + 1 < n ? P(`m${k + 1}`, 'axle') : P('hand', 'end');
        w = thread(w, `s${k + 1}`, [P(`roof${k}`, 'eye'), P(`m${k}`, 'rimL'), upTo], X0 + (k - 1) * R, `string ${k + 1}`);
    }
    return remeasure(over(w, 'load', 'm0'));
}

/**
 * A block on a table, a pulley at its edge, and a weight hanging from the
 * other end of the rope — the arrangement every book uses to introduce a
 * system of two bodies.
 *
 * Nothing new was needed in the engine for the edge pulley: a rope run is a
 * vector between two contact points and always was, so a horizontal one is
 * neither a special case nor an approximation. The table is what is new, and
 * the sheave being bolted to its END is what makes it a fixed one.
 *
 * The x arithmetic is not by eye. The run from the block's hook to the rim is
 * horizontal only when the two are at the same HEIGHT, and the rim is a radius
 * to the left of the sheave's centre — so the sheave's centre goes at the
 * block's hook height, and `thread()` is no use here because it straightens
 * runs by sliding parts sideways.
 */
export function onTable(mu: number, mass1 = 3, mass2 = 2): World {
    // Upright sides, and a top 1.4 m up — which puts it at y = 2.2.
    const tbl = surfaceAt('tbl', 'table', 2.4, 2.6, Math.PI / 2, Math.PI / 2, 1.4, mu);
    const m1 = standing('m1', tbl, 'top', -0.7, mass1, 'm₁');
    /* The block is still a POINT, so the rope leaves it at its middle — and
     * for the run across the table to be horizontal the wheel's tangent must
     * be at that height, which for a horizontal run is the wheel's centre
     * offset by its radius, vertically. */
    // The SMALL wheel, as any pulley fastened to a surface is — see
    // `SHEAVE_R_ON_FACE`. Its centre one of its own radii below the rope, which
    // puts it on the table's corner and the rope parallel to the top.
    const edge = { ...sheaveAt('edge', 3.7, m1.at.y - SHEAVE_R_ON_FACE), data: { radius: SHEAVE_R_ON_FACE, mass: 0 } };
    /* Far enough below the wheel to CLEAR it. The rope leaves the rim
     * tangentially and drops, so the weight hangs beside the wheel rather than
     * under its centre, and hung too close the two overlap — which the bench
     * reports as a separation, correctly, with a very large force. */
    const m2 = weightAt('m2', 4.0, 0.7, mass2, 'm₂');
    let w = shell(
        [tbl, m1, edge, m2],
        [bond('b1', P('edge', 'axle'), P('tbl', 'endR'))],
    );
    // Under the rim it leaves by, so the fall is vertical.
    w = { ...w, parts: w.parts.map(p => (p.id === 'm2' ? { ...p, at: { ...p.at, x: edge.at.x + SHEAVE_R_ON_FACE } } : p)) };
    const rope: Rope = { id: 'r1', type: 'rope', path: [P('m1', 'hook'), P('edge', 'rimL'), P('m2', 'hook')], length: 0, label: 'the rope' };
    return remeasure({ ...w, links: [...w.links, rope] });
}

/**
 * A wheel slung in a bight, and the angle it hangs at.
 *
 * One rope from a hook, under a slung wheel, over a fixed one, down to a load.
 * A second load hangs from the slung wheel. Both legs of the bight carry the
 * same tension, so their sideways pulls can only cancel if they make EQUAL
 * angles with the vertical — and then the two upward components have to carry
 * the sling between them:
 *
 *     2 · T · cos θ = m₂ · g       with      T = m₁ · g
 *     →   cos θ = m₂ / 2·m₁
 *
 * At 4 kg and 6 kg that is 0.75, so 41.4°, and the bench comes out at 41.41°
 * on both legs without anything having placed them there.
 *
 * The positions are the SOLVED ones, written down. A preset should open at
 * rest — a bench that rearranges itself the moment it loads teaches the reader
 * that the arrangement was not meant — and for a slanted rig there is no
 * threading rule that lands on the answer, because `thread` straightens runs
 * and these are not straight. So it was built roughly, solved, and the answer
 * recorded. `bench.test.ts` holds it to that: nothing may move.
 *
 * The hanger is slanted too, and must be: the fixed wheel is pulled sideways
 * by the bight and nothing else can take that but the rope holding it up.
 */
function slung(): World {
    const m2 = 4, M2 = 6;
    const w = shell(
        [
            // Right hook first, so it is the one called H₁ — `numbered` counts
            // in the order they appear, and the reader reads left to right.
            anchorAt('h1', 3.1055),
            anchorAt('h2', 0.9889),
            sheaveAt('p1', 2.8227, 3.2517),
            sheaveAt('p2', 2.1025, 3.0397),
            /* One pin, on the load at the end of the rope.
             *
             * The bench stands without it — that is the whole point of the
             * arrangement — so the pin is not holding anything up. It is there
             * so a reader has something to LET GO of: pull it and the bench
             * carries on standing, which is a more convincing demonstration
             * that it was standing than being told so. */
            { ...weightAt('m2', 3.0227, 0.7584, m2), pinned: true },
            weightAt('M2', 2.1025, 1.8397, M2),
        ],
        [],
    );
    const links: Link[] = [
        { id: 'hang', type: 'rope', path: [P('h1', 'eye'), P('p1', 'axle')], length: 0, label: 'the hanger' },
        { id: 'main', type: 'rope', path: [P('h2', 'eye'), P('p2', 'rimL'), P('p1', 'rimR'), P('m2', 'hook')], length: 0, label: 'the rope' },
        { id: 'load', type: 'rope', path: [P('p2', 'axle'), P('M2', 'hook')], length: 0, label: 'the sling' },
    ];
    return remeasure({ ...w, links });
}

/** A block let go on a slope, and nothing else on the bench. */
export function onRamp(mu: number, angle = Math.PI / 6, mass = 2): World {
    const ramp = surfaceAt('ramp', 'incline', 3.0, 4.0, angle, angle,
        apexHeight(4.0, angle, angle), mu);
    return remeasure(shell([ramp, standing('m1', ramp, 'right', -0.6, mass, 'm₁')], []));
}

/**
 * A movable pulley with a spring balance spliced into the fall.
 *
 * The balance is the cheapest possible proof that the multipliers are the
 * right primitive: it adds no mass, no force and no row, and it reads 24.5 N
 * beside a load that weighs 49. Being massless is what makes the rope above it
 * and the rope below it carry the same tension — so the reading is derived,
 * not asserted.
 */
function balanceRig(): World {
    let w = shell(
        [
            anchorAt('roof', 2.7), sheaveAt('p1', 3, 2.1), weightAt('load', 3, 1.1, 5),
            balanceAt('gauge', 3.3, 3.0, 'the balance'), endAt('hand', 3.3, 3.6),
        ],
        [bond('b1', P('load', 'hook'), P('p1', 'axle'))],
    );
    w = thread(w, 'r1', [P('roof', 'eye'), P('p1', 'rimL'), P('gauge', 'bottom')], 2.7, 'the fall');
    /* The line below the balance is a SECOND rope, because a balance in a line
     * is a massless body between two of them and its own force balance is what
     * makes the two tensions equal. Threading one rope through would have
     * asserted that instead of deriving it. */
    const above: Rope = { id: 'r2', type: 'rope', path: [P('gauge', 'top'), P('hand', 'end')], length: 0, label: 'above the balance' };
    w = { ...w, links: [...w.links, above] };
    return remeasure(over(over(w, 'load', 'p1'), 'hand', 'gauge'));
}

/**
 * Hold whatever would otherwise run away — and nothing else.
 *
 * A preset arrives the way a machine arrives on a bench: set up, with a hand
 * on it, waiting to be let go. That makes hold-then-release the ONE gesture
 * the bench is ever experienced through, whether you built the thing yourself
 * or loaded it.
 *
 * The minimum set, found by holding the fastest thing and asking again. Not
 * "pin everything", which is a different and much worse bench: a rope with
 * both its ends held has no tension anybody can work out — every split of the
 * load between the two hands satisfies equilibrium equally — so a reader would
 * load a machine and be told its rope carries nothing. Holding only what has
 * to be held leaves the other end hanging, and hanging is what makes a number.
 *
 * A machine that was never going to move is left alone, which is why the fixed
 * pulley and the spring balance arrive with no pins at all: there is nothing
 * for a hand to do.
 */
function held(w: World): World {
    let out = w;
    for (let i = 0; i < 8; i++) {
        const bodies = bodiesOf(out);
        const sol = solve({ world: out, bodies, constraints: constraintsOf(out, bodies) });
        if (sol.singular) return out;

        /* The one that is going DOWN, and the fastest of those — because that
         * is the one a hand goes on. Holding the side that would rise works
         * just as well arithmetically and reads as nonsense on a bench. */
        let worst: typeof bodies[number] | null = null;
        let best = -1;
        /* Only things with MASS are candidates for a hand.
         *
         * A weightless wheel has no acceleration of its own — it goes where
         * the ropes take it — so whatever the solver reports for one is either
         * a consequence of a load moving, which pinning the load settles, or
         * the wobble left by the gram of inertia a massless coordinate is lent
         * (`GRAM` in `solve.ts`). Ranked alongside the weights, that wobble
         * read as the fastest thing on the bench, and a preset that stands
         * perfectly well on its own arrived with a pin in both its pulleys. */
        bodies.filter(isFree).filter(b => b.mass > 1e-9).forEach(b => {
            const a = sol.acceleration[b.id] ?? [];
            const falling = Math.max(0, ...a);
            const moving = Math.max(0, ...a.map(Math.abs));
            if (moving <= 1e-6) return;
            // Anything descending outranks anything merely moving.
            const rank = falling > 1e-6 ? 1000 + falling : moving;
            if (rank > best) { best = rank; worst = b; }
        });
        if (!worst) return out;

        // A hand goes on the weight, not on the sheave it hangs from.
        const ids = (worst as typeof bodies[number]).partIds;
        const on = ids.find(id => byId(out, id)?.kind === 'weight') ?? ids[0];
        out = { ...out, parts: out.parts.map(p => (p.id === on ? { ...p, pinned: true } : p)) };
    }
    return out;
}

const MACHINES: Preset[] = [
    {
        key: 'fixed',
        label: 'Single fixed pulley',
        claim: 'MA 1 — it only changes the direction you pull in. The hook carries 2T.',
        build: fixedPulley,
    },
    {
        key: 'movable',
        label: 'Single movable pulley',
        claim: 'MA 2 — two rope segments share the load, so you pull half the weight through twice the distance.',
        build: movablePulley,
    },
    {
        key: 'tackle2',
        label: 'Block and tackle — 2 segments',
        claim: 'MA 2, and the pull is downwards, which is the point of the extra fixed sheave.',
        build: () => tackle(2),
    },
    {
        key: 'tackle3',
        label: 'Block and tackle — 3 segments',
        claim: 'MA 3 — three segments hold the lower block. The odd one is the tie-off on the block itself.',
        build: () => tackle(3),
    },
    {
        key: 'tackle4',
        label: 'Block and tackle — 4 segments',
        claim: 'MA 4, and you must pull 4 m of rope to raise the load 1 m.',
        build: () => tackle(4),
    },
    {
        key: 'atwood',
        label: 'Atwood machine',
        claim: 'a = (m₂ − m₁)g ⁄ (m₁ + m₂). With 4 kg and 5 kg that is 1.09 m/s² — nearly balanced, so it creeps — and T = 43.6 N.',
        build: atwood,
    },
    {
        key: 'movable-tied',
        label: 'Movable pulley, rope tied to the ceiling',
        claim: 'MA 2 again — the arrangement students most often guess is 1.',
        build: movableTied,
    },
    {
        key: 'movable2',
        label: 'Two movable pulleys',
        claim: 'MA 4 — each wheel halves what the string above it carries, so 10 kg is held with 24.5 N.',
        build: () => movableSystem(2),
    },
    {
        key: 'movable3',
        label: 'Three movable pulleys',
        claim: 'MA 8, and VR 8: the hand pulls 8 m of string to raise the load 1 m.',
        build: () => movableSystem(3),
    },
    {
        key: 'slung',
        label: 'A wheel slung in a bight',
        claim: 'Both legs pull the same, so they hang at equal angles — cos θ = m₂ ⁄ 2m₁ = 0.75, and the bench says 41.4°.',
        build: slung,
        slanted: true,
    },
    {
        key: 'table',
        label: 'Block on a table, pulley at the edge',
        claim: 'a = m₂g ⁄ (m₁ + m₂) and T = m₁m₂g ⁄ (m₁ + m₂). With 3 kg on the table and 2 kg hanging that is 3.92 m/s² and 11.76 N — and the table carries N = m₁g = 29.4 N, which the rope never changes because it pulls along the table and not across it.',
        build: () => onTable(0),
    },
    {
        key: 'table-rough',
        label: 'Block on a rough table',
        claim: 'a = (m₂ − μm₁)g ⁄ (m₁ + m₂). The same 3 kg and 2 kg with μ = 0.4 gives 1.57 m/s² instead of 3.92, and T rises to 16.5 N. Watch what happens after m₂ lands: the rope goes slack and friction alone brings m₁ to a stop. Raise μ past m₂ ⁄ m₁ = 0.67 and it never moves at all.',
        build: () => onTable(0.4),
    },
    {
        key: 'incline',
        label: 'Block on a smooth ramp',
        claim: 'a = g sin θ, and nothing in the engine computes that — gravity is resolved by the direction the block is free to travel in. At 30° it is g ⁄ 2 = 4.9 m/s², with N = mg cos θ = 17.0 N.',
        build: () => onRamp(0),
    },
    {
        key: 'incline-rough',
        label: 'Block on a rough ramp',
        claim: 'a = g(sin θ − μ cos θ). At 30° with μ = 0.25 that is 2.78 m/s². It slides at all only because tan θ = 0.58 is greater than μ; make μ larger and the block stands still, held by friction alone.',
        build: () => onRamp(0.25),
    },
    {
        key: 'balance',
        label: 'Spring balance in the fall',
        claim: 'the balance reads 24.5 N while the load weighs 49 N — mechanical advantage 2, measured rather than asserted. The balance itself has no physics: it displays a tension the engine already had.',
        build: balanceRig,
    },
];

/** Every one of them, set up and held. */
export const PRESETS: Preset[] = MACHINES.map(m => ({ ...m, build: () => held(m.build()) }));

export const openingBench = (): Preset => PRESETS.find(p => p.key === 'atwood') ?? PRESETS[0];
